import { useSyncExternalStore } from 'react';
import { updateAnalyticsConsent } from './googleAnalytics';

export const CONSENT_COOKIE = 'cmb_cookie_consent';
// Request a fresh choice after introducing the Advanced Consent Mode notice.
export const CONSENT_VERSION = 2;
export const CONSENT_DAYS = 180;
export const CONSENT_EVENT = 'cmb:cookie-consent';
export const SETTINGS_EVENT = 'cmb:cookie-settings';

export function parseConsent(value, now = Date.now()) {
    try {
        const data = JSON.parse(decodeURIComponent(value));
        if (data.version !== CONSENT_VERSION || typeof data.analytics !== 'boolean' ||
            (data.captcha !== undefined && typeof data.captcha !== 'boolean') || !Number.isFinite(data.savedAt) ||
            data.savedAt > now || now - data.savedAt >= CONSENT_DAYS * 86400000) return null;
        return { ...data, captcha: data.captcha === true };
    } catch { return null; }
}

export function readConsent() {
    try {
        const entry = document.cookie.split('; ').find(cookie => cookie.startsWith(`${CONSENT_COOKIE}=`));
        return entry ? parseConsent(entry.slice(CONSENT_COOKIE.length + 1)) : null;
    } catch { return null; }
}

export function shouldShowConsent() {
    return !readConsent();
}

export function privacySignalEnabled() {
    return navigator.globalPrivacyControl === true;
}

export function hasConsent(category) {
    return !privacySignalEnabled() && readConsent()?.[category] === true;
}

export function saveConsent(preferences) {
    const data = {
        version: CONSENT_VERSION,
        savedAt: Date.now(),
        analytics: !privacySignalEnabled() && preferences.analytics === true,
        captcha: !privacySignalEnabled() && preferences.captcha === true,
    };
    // Change storage permission immediately, before navigation or React effects.
    updateAnalyticsConsent(data.analytics);
    if (!data.analytics) clearAnalyticsCookies();
    const hostname = window.location.hostname;
    const domain = hostname === 'baling.com' || hostname.endsWith('.baling.com')
        ? '; Domain=baling.com' : '';
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(data))}; Path=/; Max-Age=${CONSENT_DAYS * 86400}; SameSite=Lax${domain}${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    window.dispatchEvent(new Event(CONSENT_EVENT));
    return data;
}

export function clearAnalyticsCookies() {
    const domains = ['', ...window.location.hostname.split('.').map((_, index, parts) => parts.slice(index).join('.')).filter(domain => domain.includes('.'))];
    for (const entry of document.cookie.split('; ')) {
        const name = entry.split('=')[0];
        if (!/^(_ga(?:_|$)|_gid$|_gat(?:_|$))/.test(name)) continue;
        for (const domain of domains) {
            document.cookie = `${name}=; Path=/; Max-Age=0${domain ? `; Domain=${domain}` : ''}`;
        }
    }
}

export function clearSiteCookies() {
    // Revoke storage permission immediately and notify mounted subscribers.
    updateAnalyticsConsent(false);
    const domains = ['', ...window.location.hostname.split('.').map((_, index, parts) => parts.slice(index).join('.')).filter(domain => domain.includes('.'))];
    const segments = (window.location.pathname || '/').split('/').filter(Boolean);
    const paths = new Set(['/']);
    for (let index = 1; index <= segments.length; index++) {
        const path = `/${segments.slice(0, index).join('/')}`;
        paths.add(path);
        paths.add(`${path}/`);
    }
    for (const entry of document.cookie.split('; ')) {
        const name = entry.split('=')[0];
        if (!name) continue;
        for (const domain of domains) {
            for (const path of paths) {
                document.cookie = `${name}=; Path=${path}; Max-Age=0${domain ? `; Domain=${domain}` : ''}`;
            }
        }
    }
    window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function openCookieSettings() {
    window.dispatchEvent(new Event(SETTINGS_EVENT));
}

// Development reset: start this origin with no saved browser preferences.
export function clearSitePreferences() {
    clearSiteCookies();
    for (const storage of ['localStorage', 'sessionStorage']) {
        try {
            window[storage].clear();
        } catch {
            // Storage can be blocked independently; still clear the other store.
        }
    }
}

const RECAPTCHA_SITE_KEY = '6LcLVigtAAAAADOLV06LeueBXClOkaPuseWtgzES';
let recaptchaReady;

export async function executeRecaptcha(action) {
    // Do not request Google's script, or execute an existing runtime, without
    // the visitor's separate form-protection consent.
    if (!hasConsent('captcha')) throw new Error('Enable Form protection in Cookie settings before submitting.');
    if (!recaptchaReady) {
        recaptchaReady = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'cmb-recaptcha';
            script.async = true;
            script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
            const fail = () => {
                window.clearTimeout(timeout);
                script.remove();
                reject(new Error('Form protection could not load. Please try again.'));
            };
            const timeout = window.setTimeout(fail, 20000);
            script.onerror = fail;
            script.onload = () => {
                if (!hasConsent('captcha') || !window.grecaptcha?.ready) { fail(); return; }
                window.grecaptcha.ready(() => {
                    window.clearTimeout(timeout);
                    resolve(window.grecaptcha);
                });
            };
            document.head.appendChild(script);
        }).catch(error => {
            recaptchaReady = undefined;
            throw error;
        });
    }
    const api = await recaptchaReady;
    // Consent can change while the script loads or token generation is pending.
    if (!hasConsent('captcha')) throw new Error('Form protection consent was withdrawn.');
    const token = await api.execute(RECAPTCHA_SITE_KEY, { action });
    if (!hasConsent('captcha')) throw new Error('Form protection consent was withdrawn.');
    return token;
}

function subscribe(callback) {
    window.addEventListener(CONSENT_EVENT, callback);
    window.addEventListener('focus', callback);
    // Also notice shared-domain changes made in another tab or subdomain.
    const timer = window.setInterval(callback, 2000);
    return () => {
        window.removeEventListener(CONSENT_EVENT, callback);
        window.removeEventListener('focus', callback);
        window.clearInterval(timer);
    };
}

export function useCookieConsent(category) {
    return useSyncExternalStore(subscribe, () => hasConsent(category), () => false);
}
